/**
 * Build a Postman Collection v2.1 from the messaging API docs catalog
 * (`MESSAGING_API_STAGES` on /agency/api-docs).
 */
import {
  MESSAGING_API_STAGES,
  type ApiDocsStage,
  type ApiEndpointDoc,
  type HttpMethod,
} from './messagingApiCatalog';

export const POSTMAN_COLLECTION_SCHEMA =
  'https://schema.getpostman.com/json/collection/v2.1.0/collection.json';

export const POSTMAN_COLLECTION_FILENAME =
  'kol-my-messaging-api.postman_collection.json';

export type PostmanVariable = {
  key: string;
  value: string;
  type?: string;
  description?: string;
};

export type PostmanUrl = {
  raw: string;
  host: string[];
  path: string[];
  variable?: Array<{ key: string; value: string }>;
};

export type PostmanRequest = {
  method: HttpMethod;
  header: Array<{ key: string; value: string; type?: string }>;
  body?: {
    mode: 'raw';
    raw: string;
    options?: { raw: { language: string } };
  };
  url: PostmanUrl;
  description?: string;
};

export type PostmanItem = {
  name: string;
  description?: string;
  request?: PostmanRequest;
  item?: PostmanItem[];
  response?: unknown[];
};

export type PostmanCollection = {
  info: {
    name: string;
    description: string;
    schema: string;
  };
  variable: PostmanVariable[];
  item: PostmanItem[];
};

const PATH_PARAM_RE = /:([A-Za-z_][A-Za-z0-9_]*)/g;

function defaultParamValue(name: string): string {
  switch (name) {
    case 'jobId':
      return 'job_id';
    case 'invoiceId':
      return 'invoice_id';
    case 'pmId':
      return 'payment_milestone_id';
    case 'threadId':
      return 'thread_id';
    case 'id':
      return 'id';
    case 'kolId':
      return 'kol_profile_id';
    case 'invitationId':
      return 'invitation_id';
    case 'organizationId':
      return 'organization_id';
    case 'templateId':
      return 'template_id';
    case 'connectionId':
      return 'connection_id';
    default:
      return name;
  }
}

/** Convert `/api/jobs/:jobId/broadcast` → Postman path parts. */
export function pathToPostmanParts(path: string): {
  rawPath: string;
  segments: string[];
  pathVariables: Array<{ key: string; value: string }>;
  paramNames: string[];
} {
  const segments: string[] = [];
  const pathVariables: Array<{ key: string; value: string }> = [];
  const paramNames: string[] = [];

  // Strip query string for path segments; keep note separately if needed
  const pathOnly = path.split('?')[0];
  const trimmed = pathOnly.replace(/^\/+/, '').replace(/\/+$/, '');

  for (const part of trimmed.split('/').filter(Boolean)) {
    const m = /^:([A-Za-z_][A-Za-z0-9_]*)$/.exec(part);
    if (m) {
      const key = m[1];
      paramNames.push(key);
      pathVariables.push({ key, value: defaultParamValue(key) });
      segments.push(`:${key}`);
    } else {
      segments.push(part);
    }
  }

  const rawPath = segments
    .map((s) => (s.startsWith(':') ? `{{${s.slice(1)}}}` : s))
    .join('/');

  return { rawPath, segments, pathVariables, paramNames };
}

function endpointDescription(endpoint: ApiEndpointDoc): string {
  const lines = [endpoint.purpose];
  if (endpoint.actor) lines.push(`\n**Actor:** ${endpoint.actor}`);
  if (endpoint.eventKeys?.length) {
    lines.push(`\n**Messaging events:** ${endpoint.eventKeys.join(', ')}`);
  }
  if (endpoint.channels?.length) {
    lines.push(`\n**Channels:** ${endpoint.channels.join(', ')}`);
  }
  if (endpoint.notes) lines.push(`\n**Notes:** ${endpoint.notes}`);
  if (endpoint.sampleResponse !== undefined) {
    lines.push(
      `\n**Sample response:**\n\`\`\`json\n${JSON.stringify(endpoint.sampleResponse, null, 2)}\n\`\`\``
    );
  }
  return lines.join('');
}

function requestName(endpoint: ApiEndpointDoc): string {
  return `${endpoint.method} ${endpoint.path}`;
}

function parseQueryKeys(path: string): string[] {
  const q = path.split('?')[1];
  if (!q) return [];
  return q
    .split('&')
    .map((pair) => pair.split('=')[0])
    .filter(Boolean);
}

export function endpointToPostmanItem(endpoint: ApiEndpointDoc): PostmanItem {
  const { rawPath, segments, pathVariables } = pathToPostmanParts(endpoint.path);
  const method = endpoint.method;
  const needsBody = method !== 'GET' && method !== 'DELETE';
  const queryKeys = parseQueryKeys(endpoint.path);

  const request: PostmanRequest = {
    method,
    header: [{ key: 'Content-Type', value: 'application/json', type: 'text' }],
    url: {
      raw:
        queryKeys.length > 0
          ? `{{baseUrl}}/${rawPath}?${queryKeys.map((k) => `${k}=`).join('&')}`
          : `{{baseUrl}}/${rawPath}`,
      host: ['{{baseUrl}}'],
      path: segments,
      ...(pathVariables.length ? { variable: pathVariables } : {}),
    },
    description: endpointDescription(endpoint),
  };

  if (queryKeys.length > 0) {
    (request.url as PostmanUrl & { query?: Array<{ key: string; value: string }> }).query =
      queryKeys.map((key) => ({ key, value: '' }));
  }

  if (needsBody) {
    const sample =
      endpoint.sampleRequest !== undefined ? endpoint.sampleRequest : {};
    request.body = {
      mode: 'raw',
      raw: JSON.stringify(sample, null, 2),
      options: { raw: { language: 'json' } },
    };
  }

  return {
    name: requestName(endpoint),
    request,
    response: [],
  };
}

function stageToFolder(stage: ApiDocsStage): PostmanItem {
  return {
    name: stage.title,
    description: [
      stage.summary,
      stage.statuses?.length ? `\nRelated statuses: ${stage.statuses.join(', ')}` : '',
    ]
      .filter(Boolean)
      .join(''),
    item: stage.endpoints.map(endpointToPostmanItem),
  };
}

function collectPathParamVariables(stages: ApiDocsStage[]): PostmanVariable[] {
  const seen = new Set<string>();
  const vars: PostmanVariable[] = [];
  for (const stage of stages) {
    for (const ep of stage.endpoints) {
      for (const match of ep.path.matchAll(PATH_PARAM_RE)) {
        const key = match[1];
        if (seen.has(key)) continue;
        seen.add(key);
        vars.push({
          key,
          value: defaultParamValue(key),
          type: 'string',
          description: `Path parameter from ${ep.path}`,
        });
      }
    }
  }
  return vars;
}

function authFolder(): PostmanItem {
  return {
    name: '0. Auth (session cookies)',
    description:
      'Log in first so Postman stores `accessToken` / `refreshToken` cookies for subsequent requests. Enable cookie jar for the API host.',
    item: [
      {
        name: 'POST /api/auth/login',
        request: {
          method: 'POST',
          header: [{ key: 'Content-Type', value: 'application/json', type: 'text' }],
          body: {
            mode: 'raw',
            raw: JSON.stringify(
              { email: 'agency@example.com', password: 'your-password' },
              null,
              2
            ),
            options: { raw: { language: 'json' } },
          },
          url: {
            raw: '{{baseUrl}}/api/auth/login',
            host: ['{{baseUrl}}'],
            path: ['api', 'auth', 'login'],
          },
          description: 'Obtain session cookies used by the messaging APIs.',
        },
        response: [],
      },
      {
        name: 'GET /api/auth/me',
        request: {
          method: 'GET',
          header: [],
          url: {
            raw: '{{baseUrl}}/api/auth/me',
            host: ['{{baseUrl}}'],
            path: ['api', 'auth', 'me'],
          },
          description: 'Verify the authenticated session.',
        },
        response: [],
      },
    ],
  };
}

export type BuildPostmanCollectionOptions = {
  /** API host without trailing slash, e.g. https://api.kol.my */
  baseUrl?: string;
  stages?: ApiDocsStage[];
  name?: string;
  includeAuth?: boolean;
};

/**
 * Build a Postman Collection v2.1 JSON object from the messaging API catalog.
 */
export function buildMessagingPostmanCollection(
  options: BuildPostmanCollectionOptions = {}
): PostmanCollection {
  const stages = options.stages ?? MESSAGING_API_STAGES;
  const baseUrl = (options.baseUrl || 'https://api.kol.my').replace(/\/+$/, '');
  const name = options.name ?? 'kol.my Messaging API';
  const includeAuth = options.includeAuth !== false;

  const endpointCount = stages.reduce((n, s) => n + s.endpoints.length, 0);

  return {
    info: {
      name,
      description: [
        'Postman collection generated from the agency Messaging API docs (`/agency/api-docs`).',
        '',
        `Includes ${endpointCount} catalog endpoints across ${stages.length} campaign stages` +
          (includeAuth ? ', plus auth helpers.' : '.'),
        '',
        '**Source of truth:** `Frontend/src/lib/messagingApiCatalog.ts` (same list as the docs page).',
        '',
        '**Auth:** Run `POST /api/auth/login` first (cookie jar enabled). APIs use httpOnly session cookies.',
        '',
        'Set collection variable `baseUrl` to your API host (no trailing slash; paths already include `/api/...`).',
      ].join('\n'),
      schema: POSTMAN_COLLECTION_SCHEMA,
    },
    variable: [
      {
        key: 'baseUrl',
        value: baseUrl,
        type: 'string',
        description: 'Backend API host (no trailing slash)',
      },
      ...collectPathParamVariables(stages),
    ],
    item: [
      ...(includeAuth ? [authFolder()] : []),
      ...stages.map(stageToFolder),
    ],
  };
}

/** Trigger a browser download of the Postman collection JSON file. */
export function downloadMessagingPostmanCollection(
  options: BuildPostmanCollectionOptions = {}
): PostmanCollection {
  const collection = buildMessagingPostmanCollection(options);
  const blob = new Blob([JSON.stringify(collection, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = POSTMAN_COLLECTION_FILENAME;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  return collection;
}
